import React, { useCallback, useMemo } from 'react';

import {
  ItemsTable,
  useSelectedItemsChanges,
} from 'ui/components/Table/ItemsTable';
import { Pagination } from 'services/search';
import { arrayHasAllDeletedObjects } from 'helpers';

import { VendorsSearchResultsProps } from './types';
import VendorsRow from './VendorsRow';
import { VENDOR_COLUMNS, VENDOR_COLUMNS_SMALL } from '../../consts';
import { createVendorsSearchActionBarOptions } from './consts';
import FBOTitleBar from 'ui/theme/components/FBOTitleBar/FBOTitleBar';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import { useSelector } from 'react-redux';
import { getActiveUser } from 'services/userV2';
import { activeUserHasPermission } from 'services/userV2/redux/selectors';
import { useFlags } from 'helpers/useFlags';
import { PermissionType } from 'services/permissions';

const VendorsSearchResults: React.FC<VendorsSearchResultsProps> = (props) => {
  const {
    activeVendorId,
    handleVendorClick,
    onAddNewPress,
    vendors,
    isLoadingVendors,
    selectedVendors,
    pagination,
    activeDate,
    setSelectedVendors,
    onPaginationChange,
    onDeleteClicked,
    onAction,
    onVendorPageAction,
  } = props;

  const activeUser = useSelector(getActiveUser);
  const flags = useFlags();
  const hasImportPermission = useSelector(
    activeUserHasPermission([
      PermissionType.VendorsEdit,
      PermissionType.VendorsCreate,
    ])
  );

  const hasExportPermission = useSelector(
    activeUserHasPermission([
      PermissionType.VendorsView,
      PermissionType.VendorsEdit,
      PermissionType.VendorsCreate,
    ])
  );

  const csvImportDisabled =
    flags.newUserEndpoint && !(activeUser.user?.admin || hasImportPermission);
  const csvExportDisabled =
    flags.newUserEndpoint && !(activeUser.user?.admin || hasExportPermission);

  const resolvedColumns = activeVendorId
    ? VENDOR_COLUMNS_SMALL
    : VENDOR_COLUMNS(activeDate);

  const allDeletedVendors = useMemo(
    () => arrayHasAllDeletedObjects(vendors),
    [vendors]
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    onPaginationChange(newPagination);
  };

  const handleSelectClick = useSelectedItemsChanges(
    selectedVendors,
    setSelectedVendors
  );

  const handleAction = useCallback(
    (action: any) => {
      onAction(action.type, action.value);
    },
    [onAction]
  );

  return (
    <>
      <FBOTitleBar title="Vendors">
        {selectedVendors.length > 0 && !activeVendorId && (
          <FBOButton
            style={{ marginRight: '8px' }}
            variant="secondary"
            color="negative"
            size="medium"
            icon="TrashCan"
            data-qa="vendor-delete"
            permissions={[PermissionType.VendorsDelete]}
            onClick={onDeleteClicked}
          >
            Delete
          </FBOButton>
        )}
        {activeVendorId !== -1 && (
          <FBOButton
            style={{ marginRight: '8px' }}
            variant={!activeVendorId ? 'primary' : 'secondary'}
            color="positive"
            size="medium"
            icon="FBOAddCircle"
            onClick={onAddNewPress}
            data-qa="vendor-add-new"
            permissions={[PermissionType.VendorsCreate]}
          >
            {!activeVendorId ? 'Add New' : ''}
          </FBOButton>
        )}
        <FBOMenuButton
          variant="secondary"
          items={createVendorsSearchActionBarOptions(
            onVendorPageAction,
            csvImportDisabled,
            csvExportDisabled
          )}
          data-qa="vendors-three-dot-menu"
        />
      </FBOTitleBar>
      <ItemsTable
        data={vendors}
        columns={resolvedColumns}
        activeId={activeVendorId}
        onSelectedChange={handleSelectClick}
        selectedItems={selectedVendors}
        onItemClick={handleVendorClick}
        selectableItems={!activeVendorId}
        isLoadingData={isLoadingVendors}
        disableSelection={allDeletedVendors}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        RenderCustomRow={VendorsRow}
        onAction={handleAction}
        dataQa="vendor-table"
        emptyTableText={
          !activeVendorId
            ? "ADD NEW ENTRY BY PRESSING 'ADD NEW'"
            : 'NO VENDORS AVAILABLE'
        }
        showEnhancedTablePaginationActions={!Boolean(activeVendorId)}
      />
    </>
  );
};

export default VendorsSearchResults;
